<script setup lang="ts">
import { ButtonProps } from "~/components/Utils/Button.props"
import { SuggestionsProps } from "~/components/Search/Utils/Suggestions.props"
import { mapProducts, mapProductsAlgolia } from "~/utils/mapProducts"
import { customPaths } from "~/utils/constants"
import { addSearchTermIntoCookie } from "~/utils/cookies/searchCookies"
import { NotificationAlertProps } from "~/components/NotificationAlert.props"
import { checkEnvSite } from "~~/server/utils/getAlgoliaConfig"
import { buildExcludeFilter, getUserId } from "~/utils/algolia"
import { algoliaSearch } from "~~/config/algolia"
import {
  getSearchMethod,
  searchAction,
  searchMethod,
  setSearchMethod,
  debounceTimeout
} from "~/utils/analytics/search"
import { SearchResponse } from "@algolia/client-search"
import { ResultsProps } from "~/components/ProductTile/Overall/Results.props"
import { useBrandSuggestions } from "~/composables/useBrandSuggestions"
import { useCategorySuggestions } from "~/composables/useCategorySuggestions"
import { useQuerySuggestions } from "~/composables/useQuerySuggestions"
import { truncateString } from "~/utils/truncateString"

const SUGGESTION_MAX_LENGTH = 22 // 25 - ellipsis (3)

// Suggestions
type Suggestion = {
  query: string
  objectID: string
}

type BrandSuggestion = {
  brandName: string
  objectID: string
  slug?: string
}

type CategorySuggestion = {
  categoryName: string
  algoliaTitle: string
  objectID: string
  slug?: string
}

const indexName = ref(`TOP_PRODUCTS${checkEnvSite()}`)
const indexSuggestions = ref(`TOP_PRODUCTS${checkEnvSite()}_query_suggestions`)
const indexBrand = ref(`TOP_BRANDS${checkEnvSite()}_stock-desc`)
const indexCategory = ref(`TOP_CATEGORIES${checkEnvSite()}`)
const { sendEventViewSearchResults } = useGA4SiteEvents()
const { getAnalyticsTagsForAutocomplete } = useAlgoliaAnalytics()
const algoliaAnalyticsTags = getAnalyticsTagsForAutocomplete()

const { t } = useI18n()
const searchPlaceholder = computed(() =>
  typeof t === "function" ? t("searchbar.placeholder") : "searchbar.placeholder"
)
const searchLastSearchesTitle = computed(() =>
  typeof t === "function"
    ? t("searchbar.labelLastSearchs")
    : "searchbar.labelLastSearchs"
)
const searchRequestProductText = computed(() =>
  typeof t === "function"
    ? t("searchbar.requestProduct")
    : "searchbar.requestProduct"
)
const { notifications, addNotification, removeNotification } = useNotification()
const { scrollLock, scrollUnlock } = useScrollLocker()
const route = useRoute()
const user = getUserId()
const queryState = useState("searchbar-query-state", () => "")
const lastQueries = useStatefulCookie<string[]>("lastQueries", {
  maxAge: 2592000,
  default: () => []
})
const { isOpen, switchState } = useUIState("focus-search")
const { switchState: hasAddedFromAlgoliaSearchBar } = useUIState(
  "added-from-algolia-searchbar"
)
const { querySuggestions } = useQuerySuggestions()
const { brandSuggestions } = useBrandSuggestions()
const { categorySuggestions } = useCategorySuggestions()
const overlayAlgoliaClient = algoliaSearch().client
const loadingSearchResults = useState("loading-search-results", () => false)
const cameFromSearch = ref(false)
const setCameFromSearch = (val: boolean) => {
  cameFromSearch.value = val
  sessionStorage.setItem("came-from-search", String(val))
}
const disabled = ref(false) // kept for potential future use
const searchbarModal = ref(null)
const isClosable = ref(false)
const localQuery = ref()
const searchInputValue = ref(queryState.value)
const timerId = ref()
const searchbarSuggestionItems = ref<ResultsProps[]>([])

const searchQueries = computed(() => ({
  requests: [
    {
      indexName: indexName.value,
      query: queryState.value,
      hitsPerPage: 6,
      clickAnalytics: true,
      analyticsTags: algoliaAnalyticsTags,
      highlightPostTag: "__/ais-highlight__",
      highlightPreTag: "__ais-highlight__",
      userToken: user,
      ...buildExcludeFilter({ excludeOutOfMarket: true })
    },
    {
      indexName: indexSuggestions.value,
      query: queryState.value,
      hitsPerPage: 3,
      clickAnalytics: true,
      highlightPostTag: "__/ais-highlight__",
      highlightPreTag: "__ais-highlight__",
      userToken: user,
      ...buildExcludeFilter({ excludeOutOfMarket: true })
    },
    {
      indexName: indexBrand.value,
      query: queryState.value,
      hitsPerPage: 3,
      clickAnalytics: true,
      highlightPostTag: "__/ais-highlight__",
      highlightPreTag: "__ais-highlight__",
      userToken: user,
      ...buildExcludeFilter({ excludeOutOfMarket: true })
    },
    {
      indexName: indexCategory.value,
      query: queryState.value,
      hitsPerPage: 3,
      clickAnalytics: true,
      highlightPostTag: "__/ais-highlight__",
      highlightPreTag: "__ais-highlight__",
      userToken: user,
      ...buildExcludeFilter({ excludeOutOfMarket: true })
    }
  ]
}))

const algoliaSearchResults: globalThis.ComputedRef<
  Promise<
    | [
        SearchResponse<ResultsProps>,
        SearchResponse<Suggestion>,
        SearchResponse<BrandSuggestion>,
        SearchResponse<CategorySuggestion>
      ]
    | null
  >
> = computed(async () => {
  const val = queryState.value || ""

  if (!val || val.length < 3) {
    return null
  }

  loadingSearchResults.value = true
  try {
    const results = await overlayAlgoliaClient.search(searchQueries.value)

    return results.results as [
      SearchResponse<ResultsProps>,
      SearchResponse<Suggestion>,
      SearchResponse<BrandSuggestion>,
      SearchResponse<CategorySuggestion>
    ]
  } finally {
    loadingSearchResults.value = false
  }
})

const lastSearch = computed<SuggestionsProps["suggestions"]>(() => {
  const lastQueriesValue = lastQueries.value

  if (!lastQueriesValue || !Array.isArray(lastQueriesValue)) {
    return []
  }

  return lastQueriesValue
    .map((query: string) => ({
      text: query.charAt(0).toUpperCase() + query.slice(1)
    }))
    .slice(0, 3)
})

const suggestions = computedAsync(async () => {
  const results = await algoliaSearchResults.value
  const s = results?.[1]
  return transformSuggestions(s?.hits || [])
})

const brandSearchSuggestions = computedAsync(async () => {
  const results = await algoliaSearchResults.value
  const b = results?.[2]
  const hits = b?.hits || []

  const validBrands = hits.filter((hit) => (hit?.stats?.productCount || 0) > 0)

  return transformBrandSuggestions(validBrands)
})

const categorySearchSuggestions = computedAsync(async () => {
  const results = await algoliaSearchResults.value
  const c = results?.[3]
  return transformCategorySuggestions(c?.hits || [])
})

const products = computedAsync<ResultsProps[]>(async () => {
  const results = await algoliaSearchResults.value
  const p = results?.[0]
  return mapProductsAlgolia(p?.hits || [], p?.queryID) as ResultsProps[]
}, [])

const productsResults = computedAsync(async () => {
  const results = await algoliaSearchResults.value
  return results?.[0]
})

const hasQuery = computed(() => {
  return (queryState.value || "").length > 2
})

const hasQueryAndProducts = computed(() => {
  return hasQuery.value && !!products.value?.length
})

const productsCardsProps = computed(() => {
  if (hasQueryAndProducts.value) {
    return {
      title: t("searchbar.products"),
      cards: products.value,
      moreProducts: {
        text: t("searchbar.showAllResults"),
        path: `${customPaths.search}?query=${queryState.value}`
      },
      suggestions: true
    }
  } else if (hasQuery.value) {
    return {
      title: t("searchbar.moreSold"),
      cards: searchbarSuggestionItems.value,
      moreProducts: {
        text: t("searchbar.showMostSold"),
        path: `${customPaths.mostSold}`
      },
      suggestions: false
    }
  }

  return {
    title: t("searchbar.moreSold"),
    cards: searchbarSuggestionItems.value
  }
})

const emit = defineEmits<{ (e: "onClose"): void }>()

const closeSearchbar = (restoreScroll: boolean) => {
  const activeElement = document.activeElement as HTMLElement
  activeElement?.blur()
  notifications.value.splice(0, notifications.value.length)
  scrollUnlock(restoreScroll)
  switchState(false)
  setCameFromSearch(false)
}

const handleClose = () => closeSearchbar(true)

const showSearchPopup = () => {
  scrollLock()
  switchState(true)
}

const goToCart = () => {
  navigateTo(customPaths.cart)
}

const handleAddToCartNotification = (productName: string) => {
  hasAddedFromAlgoliaSearchBar(true)

  const notification: NotificationAlertProps = {
    id: Math.random(),
    type: "success",
    notificationAlertText: t(
      "dialogsAndModals.addToCart.notificationAddToCartShort"
    ),
    ctaFunction: goToCart,
    cta: t("dialogsAndModals.addToCart.notificationLinkToCart"),
    ctaInline: true,
    isWithCloseIcon: true,
    small: true,
    closeCallback: () => {
      hasAddedFromAlgoliaSearchBar(false)
    }
  }

  addNotification(notification)
}

// GTM Search Events
const { event, sendEvent } = useGAEvent(
  `track_view_search_results`,
  "custom_event"
)
// Fired when clicking the search cta with the search term and results count.
const ga4EventViewSearchResults = (
  searchTerm: string,
  searchResultsNumber: number
) => {
  event.value = {
    event: "view_search_results",
    action: "view_search_results",
    search_term: searchTerm,
    search_results_number: searchResultsNumber
  }

  lastQueries.value = addSearchTermIntoCookie(lastQueries.value, searchTerm)
  sendEvent({ clearEcommerce: false })
}

const viewResults = (searchTerm: string, searchResultsNumber: number) => {
  ga4EventViewSearchResults(searchTerm, searchResultsNumber)
  handleClose()
}

const handleBrandSuggestion = (text: string) => {
  setCameFromSearch(true)
}

onKeyStroke(
  "Escape",
  (e) => {
    if (isClosable.value) handleClose()
  },
  { dedupe: false }
)

const debounceRefine = (val: string | undefined) => {
  if (timerId.value) {
    clearTimeout(timerId.value)
  }
  // Immediate reset when clearing via the X button
  if (!val) {
    queryState.value = ""
    return
  }
  if (localQuery.value != queryState.value) {
    timerId.value = setTimeout(() => {
      queryState.value = val || ""
    }, debounceTimeout)
  }
}

watch(
  () => queryState.value,
  (newQuery) => {
    if (searchInputValue.value !== newQuery) {
      searchInputValue.value = newQuery
    }
  }
)

watch(searchInputValue, (newValue) => {
  debounceRefine(newValue)
})

const applyQuery = (text: string, method: string, action: string) => {
  setSearchMethod(method, action)
  queryState.value = text
  lastQueries.value = addSearchTermIntoCookie(lastQueries.value, text)

  navigateTo({
    path: customPaths.search,
    ...(text ? { query: { query: text } } : {})
  })
}

const transformSuggestions = (suggestions: Suggestion[]): ButtonProps[] => {
  return suggestions.map((suggestion) => ({
    text: truncateString(suggestion.query, SUGGESTION_MAX_LENGTH),
    objectID: suggestion.objectID,
    isSelectable: false
  }))
}

const transformBrandSuggestions = (
  suggestions: BrandSuggestion[]
): ButtonProps[] => {
  return suggestions.map((suggestion) => ({
    text: truncateString(suggestion.brandName, SUGGESTION_MAX_LENGTH),
    objectID: suggestion.objectID,
    path: suggestion.slug ? `/b-${suggestion.slug}?from=search` : undefined
  }))
}

const transformCategorySuggestions = (
  suggestions: CategorySuggestion[]
): ButtonProps[] => {
  return suggestions.map((suggestion) => ({
    text: truncateString(
      suggestion.algoliaTitle || suggestion.categoryName,
      SUGGESTION_MAX_LENGTH
    ),
    objectID: suggestion.objectID,
    isSelectable: false,
    path: suggestion.slug ? `/c-` + suggestion.slug : undefined
  }))
}

// On navigation the router places the new page: the pre-search offset belongs to the old one
watch(
  () => route.fullPath,
  () => closeSearchbar(false)
)

watch(isOpen, (value) => {
  if (value && !isClosable.value) {
    useTimeoutFn(() => {
      isClosable.value = true
    }, 300)
  } else {
    isClosable.value = false
  }
})

watch(
  () => isOpen.value,
  (openValue) => {
    if (!openValue || !!searchbarSuggestionItems.value.length) {
      searchbarSuggestionItems.value = []
      return
    }

    useFetch<SearchResponse>("/api/algolia/getPopularItems/6").then((res) => {
      if (!res.data.value) {
        searchbarSuggestionItems.value = []
        return
      }

      const hits = res.data.value.hits
      const queryID = res.data.value.queryID || null
      searchbarSuggestionItems.value = mapProducts(
        hits,
        queryID,
        "ALGOLIA"
      ) as ResultsProps[]
    })
  }
)

watch(
  () => [productsResults.value, queryState.value],
  ([newProductsResults, newQuery]) => {
    if (
      typeof newQuery !== "string" ||
      newQuery.length < 3 ||
      !newProductsResults
    ) {
      return
    }

    setSearchMethod(searchMethod.query)
    sendEventViewSearchResults(
      getSearchMethod(),
      newQuery,
      (newProductsResults as SearchResponse<ResultsProps>).nbHits || 0
    )
  }
)

onMounted(() => {
  cameFromSearch.value = sessionStorage.getItem("came-from-search") === "true"
})
</script>

<template>
  <div
    @keyup.enter="handleClose"
    class="
      search-searchbar_focused
      customHeight
      w-full
      overflow-y-auto
      overscroll-none
      md:h-auto
      md:overscroll-auto
    "
  >
    <div
      class="
        wrapper-notifications
        padded
        fixed
        left-4
        right-4
        top-[160px]
        z-[999998]
        flex flex-col
        gap-2
        p-0
        md:right-[24px]
        md:top-[38px]
        md:gap-4
      "
      v-if="isOpen"
    >
      <TransitionGroup name="opacity">
        <NotificationAlert
          class="flex justify-end"
          v-for="notification in notifications"
          :key="notification.id"
          v-bind="notification"
          @on-close="removeNotification(notification.id)"
        />
      </TransitionGroup>
    </div>

    <div ref="searchbarModal">
      <div class="flex !h-fit flex-col md:block">
        <div
          class="
            bg-white
            flex
            h-[60px]
            shrink-0
            items-center
            gap-1
            shadow-01
            md:relative
            md:h-auto
          "
        >
          <Transition name="opacity">
            <UtilsButton
              theme="ghost-black"
              v-show="isOpen"
              class="z-50 md:hidden"
              @click="handleClose"
            >
              <template #icon>
                <UtilsIcon name="ArrowLeft.svg" class="h-6 w-6" />
              </template>
            </UtilsButton>
          </Transition>
          <div class="flex-1">
            <ClientOnly>
              <SearchSearchbar
                v-model="searchInputValue"
                v-bind="{
                  name: 'main-search',
                  label: 'Search label',
                  placeholder: searchPlaceholder,
                  isPlaceholderAnimated: false,
                  disabled: cameFromSearch,
                  showTextIcon: false
                }"
                :model-value="queryState"
                :trigger-focus="isOpen"
                @on-focus-in="showSearchPopup"
                :class="`relative z-40`"
                :is-open="isOpen"
                :forceQuery="queryState"
              />
              <template #fallback>
                <SkeletonAnimatedPlaceholder
                  height="47px"
                  width="100%"
                  borderRadius="30px"
                />
              </template>
            </ClientOnly>
            <Transition name="opacity">
              <div
                v-if="isOpen"
                class="
                  bg-black-main/30
                  fixed
                  left-0
                  top-0
                  z-30
                  hidden
                  h-screen
                  w-full
                  md:block
                "
                @click="handleClose()"
              />
            </Transition>
          </div>
        </div>
      </div>
      <Transition name="opacity">
        <div
          v-show="isOpen"
          class="
            algolia-search-bar-modal
            bg-white
            fixed
            left-0
            z-40
            h-screen
            max-h-[90dvh]
            w-full
            overflow-y-auto
            pb-[60px]
            md:absolute
            md:left-auto
            md:mr-2
            md:mt-1
            md:h-auto
            md:w-full
            md:max-w-[840px]
            md:rounded-[16px]
            md:pb-0
          "
        >
          <SearchOverlay :key="queryState" class="h-full md:h-auto">
            <div
              :class="`bg-white absolute left-0 top-0 z-50 h-full w-full`"
              v-if="loadingSearchResults"
            >
              <UtilsLoader
                dimensions="big"
                type="spinner"
                class="justify-center align-middle"
              />
            </div>
            <div
              :class="{
                'px-4 md:grid md:grid-cols-4 md:gap-4 md:px-6': true,
                'md:min-h-[67px]': productsResults?.nbHits != 1
              }"
            >
              <SearchUtilsSuggestionsGroups
                v-if="
                  (productsResults?.nbHits ?? 0) > 0 ||
                  (queryState || '').length < 3
                "
                class="col-span-1 pb-4 md:pb-0"
                :title="searchLastSearchesTitle"
                :queryState="queryState"
                :lastSearch="lastSearch"
                :querySuggestions="querySuggestions"
                :categorySuggestions="categorySuggestions"
                :brandSuggestions="brandSuggestions"
                :suggestions="suggestions"
                :categorySearchSuggestions="categorySearchSuggestions"
                :brandSearchSuggestions="brandSearchSuggestions"
                @click-recent="
                  applyQuery($event, searchMethod.recent, searchAction.symptoms)
                "
                @click-popular="
                  applyQuery(
                    $event,
                    searchMethod.popular,
                    searchAction.symptoms
                  )
                "
                @click-brand="handleBrandSuggestion($event)"
                @click-suggestions="
                  applyQuery(
                    $event,
                    searchMethod.suggestions,
                    searchAction.symptoms
                  )
                "
              />
              <SearchUtilsNoResults
                class="col-span-2 pt-4 md:pt-6"
                :searchTerm="queryState"
                :cta="customPaths.requestNewProduct"
                :ctaText="searchRequestProductText"
                v-if="
                  (queryState || '').length > 2 && productsResults?.nbHits == 0
                "
                @on-click-cta="handleClose()"
              />
            </div>
            <div :class="['grid-search p-4 md:p-6']">
              <SearchUtilsProducts
                class="col-span-full"
                v-if="hasQueryAndProducts"
                v-bind="productsCardsProps"
                @on-click-show-result="
                  viewResults(queryState || '', products?.length)
                "
                @on-close-modal="handleClose()"
                @on-add-to-cart="handleAddToCartNotification($event)"
              />
              <SearchUtilsProducts
                v-else-if="hasQuery"
                class="col-span-full"
                v-bind="productsCardsProps"
                @on-close-modal="handleClose()"
                @on-add-to-cart="handleAddToCartNotification($event)"
              />
              <SearchUtilsProducts
                v-else
                class="col-span-full"
                v-bind="productsCardsProps"
                @on-close-modal="handleClose()"
                @on-add-to-cart="handleAddToCartNotification($event)"
              />
            </div>
          </SearchOverlay>
        </div>
      </Transition>
    </div>
  </div>
</template>

<style lang="scss" scoped>

.search-searchbar_focused {
  :deep(.ais-Stats, .ais-InstantSearch) {
    @apply h-full;
  }
}

</style>
